import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FileText, Trash2, Edit2, Archive, Eye } from 'lucide-react';
import type { Document } from '@/types/auth';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

const CATEGORIES = [
  { value: 'rules', label: 'Rules' },
  { value: 'agreement', label: 'Agreement' },
  { value: 'policy', label: 'Policy' },
  { value: 'form', label: 'Form' },
  { value: 'other', label: 'Other' },
] as const;

export function DocumentManager() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [viewingDoc, setViewingDoc] = useState<Document | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    category: 'rules' as Document['category'],
    content: '',
    version: '1.0',
  });

  useEffect(() => {
    fetchDocuments();
  }, [showArchived]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const url = showArchived
        ? `${API_BASE}/api/documents`
        : `${API_BASE}/api/documents?activeOnly=true`;
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (err) {
      setError('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const currentUser = JSON.parse(sessionStorage.getItem('lsba_user') || '{}');

    try {
      setLoading(true);

      if (editingId) {
        // Update document
        const response = await fetch(`${API_BASE}/api/documents`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: editingId,
            ...formData,
            lastModifiedBy: currentUser.username || 'admin',
          }),
        });

        if (response.ok) {
          setSuccess('Document updated successfully');
          setEditingId(null);
          resetForm();
          await fetchDocuments();
        } else {
          const data = await response.json();
          setError(data.error || 'Failed to update document');
        }
      } else {
        // Create document
        const response = await fetch(`${API_BASE}/api/documents`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...formData,
            uploadedBy: currentUser.username || 'admin',
          }),
        });

        if (response.ok) {
          setSuccess('Document created successfully');
          resetForm();
          await fetchDocuments();
        } else {
          const data = await response.json();
          setError(data.error || 'Failed to create document');
        }
      }
    } catch (err) {
      setError('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (doc: Document) => {
    setEditingId(doc.id);
    setFormData({
      title: doc.title,
      category: doc.category,
      content: doc.content,
      version: doc.version,
    });
  };

  const handleArchive = async (id: string) => {
    if (!confirm('Archive this document?')) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/documents`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, permanent: false }),
      });

      if (response.ok) {
        setSuccess('Document archived successfully');
        await fetchDocuments();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to archive document');
      }
    } catch (err) {
      setError('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Permanently delete this document? This cannot be undone!')) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/documents`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, permanent: true }),
      });

      if (response.ok) {
        setSuccess('Document permanently deleted');
        await fetchDocuments();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete document');
      }
    } catch (err) {
      setError('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      category: 'rules',
      content: '',
      version: '1.0',
    });
    setEditingId(null);
  };

  return (
    <div className="space-y-6">
      {viewingDoc ? (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>View Document</CardTitle>
              <Button variant="outline" onClick={() => setViewingDoc(null)}>
                Close
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h2 className="text-2xl font-bold">{viewingDoc.title}</h2>
                <div className="flex gap-4 mt-2 text-sm text-gray-600">
                  <span>Category: {viewingDoc.category}</span>
                  <span>Version: {viewingDoc.version}</span>
                  <span>
                    Uploaded: {new Date(viewingDoc.uploadedAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div className="border-t pt-4">
                <pre className="whitespace-pre-wrap font-sans">{viewingDoc.content}</pre>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {editingId ? 'Edit Document' : 'Create New Document'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              {success && (
                <Alert className="mb-4 bg-green-50 text-green-900 border-green-200">
                  <AlertDescription>{success}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="title">Document Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      placeholder="Enter document title"
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <select
                      id="category"
                      className="w-full px-3 py-2 border rounded-md"
                      value={formData.category}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          category: e.target.value as Document['category'],
                        })
                      }
                      required
                    >
                      {CATEGORIES.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="version">Version</Label>
                  <Input
                    id="version"
                    value={formData.version}
                    onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                    placeholder="1.0"
                  />
                </div>

                <div>
                  <Label htmlFor="content">Content *</Label>
                  <textarea
                    id="content"
                    className="w-full min-h-[200px] px-3 py-2 border rounded-md font-mono text-sm"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    required
                    placeholder="Enter document content..."
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Processing...' : editingId ? 'Update Document' : 'Create Document'}
                  </Button>
                  {editingId && (
                    <Button type="button" variant="outline" onClick={resetForm}>
                      Cancel
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Documents</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowArchived(!showArchived)}
                >
                  {showArchived ? 'Hide Archived' : 'Show Archived'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading && !documents.length ? (
                <p className="text-center text-gray-500">Loading...</p>
              ) : documents.length === 0 ? (
                <p className="text-center text-gray-500">No documents yet</p>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className={`border rounded-lg p-4 ${
                        !doc.isActive ? 'bg-gray-100 opacity-60' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg flex items-center gap-2">
                            {doc.title}
                            {!doc.isActive && (
                              <span className="text-xs bg-gray-500 text-white px-2 py-1 rounded">
                                Archived
                              </span>
                            )}
                          </h3>
                          <div className="flex gap-4 mt-1 text-sm text-gray-600">
                            <span>Category: {doc.category}</span>
                            <span>Version: {doc.version}</span>
                            <span>By: {doc.uploadedBy}</span>
                            <span>
                              {new Date(doc.uploadedAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setViewingDoc(doc)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(doc)}
                            disabled={!doc.isActive}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          {doc.isActive ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleArchive(doc.id)}
                            >
                              <Archive className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDelete(doc.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
